import { Trans } from '@lingui/react/macro';
import type { BusinessDataConnection } from '../../services/businessDataConnectionService';
import type { BusinessDataOperation } from '../../services/businessDataService';
import { RebuildIcon, SearchIcon } from './icons';

interface Props {
  conn: BusinessDataConnection;
  scanJob?: BusinessDataOperation;
  isScanning: boolean;
  onScan: (conn: BusinessDataConnection) => void;
  onReconnect: (conn: BusinessDataConnection) => void;
  onUpgrade?: () => void;
  onDisconnect: (conn: BusinessDataConnection) => void;
}

const DISCONNECT = 'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10';

// Primary scan-button content by state — the badge carries the spinner, so this
// button only changes label/icon (and is disabled while scanning).
function scanButtonContent(isScanning: boolean, isError: boolean) {
  if (isScanning) return <><Trans>Scanning…</Trans></>;
  if (isError) return <><RebuildIcon className="w-3 h-3" /><Trans>Retry scan</Trans></>;
  return <><SearchIcon className="w-3 h-3" /><Trans>Scan</Trans></>;
}

// Action row for a connected service card.
// - Auth failed (connection status 'error') → Reconnect (re-enter credentials) + Disconnect.
// - Otherwise → Scan / "Retry scan" (last scan errored) / disabled while running, + Disconnect.
export function ConnectionActions({ conn, scanJob, isScanning, onScan, onReconnect, onUpgrade, onDisconnect }: Props) {
  if (conn.status === 'error') {
    return (
      <div className="flex gap-2">
        <button
          onClick={() => onReconnect(conn)}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-colors bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-500/20"
        >
          <RebuildIcon className="w-3 h-3" /><Trans>Reconnect</Trans>
        </button>
        <button onClick={() => onDisconnect(conn)} className={DISCONNECT}><Trans>Disconnect</Trans></button>
      </div>
    );
  }

  const isError = scanJob?.status === 'error';
  return (
    <div className="flex gap-2">
      <button
        onClick={() => onScan(conn)}
        disabled={isScanning}
        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
          isError
            ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/20'
            : 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-500/20'
        }`}
      >
        {scanButtonContent(isScanning, isError)}
      </button>
      {onUpgrade && (
        <button onClick={onUpgrade} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10">OAuth</button>
      )}
      <button onClick={() => onDisconnect(conn)} className={DISCONNECT}><Trans>Disconnect</Trans></button>
    </div>
  );
}
